import React, { createContext, ReactNode, useContext } from "react";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import SettingsProvider from "./SettingsProvider";
import { useDispatch } from "react-redux";
import ModalKeys from "../../../const/ModalKeys";
import {
  addModal,
  removeModal,
} from "../../../libraries/state_reducers/widget/ModalsReducer";
import CacheStorage from "../../../service/storage/CacheStorage";
import StorageKeys from "../../../const/StorageKeys";
import { appRoutes } from "../../../routes/routes";
import { termsExternalLinks } from "../../../const/ExternalLinks";
import Loader from "../../../widget/common/ui/Loader";
import { ResponsiveDeviceType } from "../../../widget/layouts/ScreenMedia";

type SettingsTypes = {
  menuList: Array<SettingsItemTypes>;
  languages: Array<SettingLanguageType> | [];
  handleClick: (menu: SettingsItemTypes) => void;
  changeLanguage: (lang: string) => void;
};

type SettingsItemTypes = {
  icon: string;
  name: string;
  url?: string | undefined;
};

export type SettingLanguageType = {
  name: string;
  country: string;
  lang: string;
};

const SettingsContext = createContext<SettingsTypes | undefined>(undefined);

export const SettingsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const languages: Array<SettingLanguageType> | [] = [
    { name: "english", country: "US", lang: "en" },
    { name: "arabic", country: "AE", lang: "ar" },
  ];

  const menuList: Array<SettingsItemTypes> = [
    //{ icon: "change_branch", name: "change_default_branch" },
    { icon: "language", name: "language_settings" },
    { icon: "mobile_updates", name: "check_updates" },
    {
      icon: "privacy",
      name: "privacy_policy",
      url: termsExternalLinks.privacy_policy,
    },
    {
      icon: "terms",
      name: "terms_of_use",
      url: termsExternalLinks.term_of_use,
    },
    {
      icon: "disclaimer",
      name: "disclaimer",
      url: termsExternalLinks.disclaimer,
    },
    {
      icon: "agreement",
      name: "end_user_license_agreement",
      url: termsExternalLinks.enduser_agreement,
    },
  ];

  const handleClick = async (menu: SettingsItemTypes) => {
    if (menu.url !== undefined) {
      if (type === 3) {
        UtilityHelper.navigateTab(menu.url);
      } else {
        dispatch(
          addModal({
            id: ModalKeys.web_view,
            size: "lg",
            screenMobileView: true,

            title: menu.name,
            webViewUrl: menu.url,
          })
        );
      }
    } else if (menu.name === "check_updates") {
      Loader.modal();
      await SettingsProvider.checkUpdatesAPI();
      Loader.modal(false);
    } else {
      if (menu.name === "language_settings") {
        dispatch(
          addModal({
            id: ModalKeys.settings_language,
            size: "md",
          })
        );
      } else if (menu.name === "change_default_branch") {
        SettingsProvider.navigate(
          appRoutes.routeNameSettingsChangeDefaultBranch
        );
      }
    }
  };

  const changeLanguage = (lang: string) => {
    CacheStorage.saveValue(StorageKeys.language, lang);
    dispatch(removeModal(ModalKeys.settings_language));

    SettingsProvider.navigate(0);
  };

  return (
    <SettingsContext.Provider
      value={{
        menuList,
        languages,
        handleClick,
        changeLanguage,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SettingsContextImport = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(
      "SettingsContextImport must be used within a SettingsContextProvider"
    );
  }
  return context;
};
